export const EXAM_CATEGORIES = [
    { value: 'weekly', label: 'Weekly exam' },
    { value: 'monthly', label: 'Monthly exam' },
    { value: 'mock', label: 'Mock exam' },
    { value: 'practice', label: 'Practice exam' },
    { value: 'topic', label: 'Topic test' },
    { value: 'other', label: 'Other exam' },
];

export const EXAM_SORTS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'title-asc', label: 'Name: A–Z' },
    { value: 'title-desc', label: 'Name: Z–A' },
    { value: 'duration-asc', label: 'Duration: shortest first' },
    { value: 'duration-desc', label: 'Duration: longest first' },
];

export function normalizeExamCategory(value) {
    return EXAM_CATEGORIES.some(category => category.value === value) ? value : 'auto';
}

export function inferExamCategory(title = '') {
    const name = String(title).toLowerCase();
    if (/\b(monthly|month)(?:\b|\d)/.test(name)) return 'monthly';
    if (/\b(weekly|week)(?:\b|\d)/.test(name)) return 'weekly';
    if (/\b(mock|mocks)\b/.test(name)) return 'mock';
    if (/\b(practice|practise|revision)\b/.test(name)) return 'practice';
    if (/\b(topic|chapter|unit)(?:\b|\d)/.test(name)) return 'topic';
    return 'other';
}

export const examDisplayTitle = exam => String(exam?.publishedTitle || exam?.title || 'Untitled exam');
export const examDisplayMode = exam => exam?.publishedMode || exam?.mode || 'on-demand';
export function examDisplayDuration(exam) {
    const value = Number(exam?.publishedDuration ?? exam?.duration);
    return Number.isFinite(value) && value > 0 ? value : null;
}

export function examCategory(exam) {
    // Unpublished draft edits must not change the category students see.
    const active = Array.isArray(exam?.versions) ? exam.versions.find(version => version.id === exam.activeVersionId) : null;
    const published = exam?.activeVersionId || exam?.publishedTitle || exam?.publishedQuestionsUrl;
    const choice = normalizeExamCategory(exam?.publishedCategory ?? active?.category ?? (published ? 'auto' : exam?.category));
    return choice === 'auto' ? inferExamCategory(examDisplayTitle(exam)) : choice;
}

export const examCategoryLabel = value => EXAM_CATEGORIES.find(category => category.value === value)?.label || 'Other exam';

export function examDateMillis(value) {
    try {
        const result = typeof value?.toMillis === 'function' ? value.toMillis()
            : typeof value?.toDate === 'function' ? value.toDate().getTime()
            : value instanceof Date ? value.getTime()
            : typeof value?.seconds === 'number' ? value.seconds * 1000
            : typeof value === 'number' ? value
            : typeof value === 'string' ? Date.parse(value) : NaN;
        return Number.isFinite(result) ? result : null;
    } catch { return null; }
}

const compareNames = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const compareValues = (a, b, descending) => {
    // Missing dates/durations always go last, whichever direction is selected.
    if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
    return descending ? b - a : a - b;
};

export function sortExams(exams, sort = 'newest') {
    return [...exams].sort((a, b) => {
        let order = 0;
        if (sort.startsWith('title')) order = compareNames(examDisplayTitle(a), examDisplayTitle(b)) * (sort === 'title-desc' ? -1 : 1);
        else if (sort.startsWith('duration')) order = compareValues(examDisplayDuration(a), examDisplayDuration(b), sort === 'duration-desc');
        else order = compareValues(examDateMillis(a.createdAt), examDateMillis(b.createdAt), sort !== 'oldest');
        return order || compareNames(examDisplayTitle(a), examDisplayTitle(b)) || compareNames(String(a.id), String(b.id));
    });
}

export function availableStudentExams(exams, papers) {
    const assigned = new Set(papers.map(paper => paper.id));
    return exams.filter(exam => assigned.has(exam.paperId) && !exam.isHidden && !(exam.versions !== undefined && !exam.activeVersionId));
}

export function filterStudentExams(exams, papers, attemptedIds, filters) {
    const paperNames = new Map(papers.map(paper => [paper.id, paper.name || '']));
    const terms = String(filters.search || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    return sortExams(exams.filter(exam => {
        if (filters.paper && filters.paper !== exam.paperId) return false;
        if (filters.category && filters.category !== examCategory(exam)) return false;
        if (filters.mode && filters.mode !== examDisplayMode(exam)) return false;
        if (filters.attempt === 'attempted' && !attemptedIds.has(exam.id)) return false;
        if (filters.attempt === 'not-attempted' && attemptedIds.has(exam.id)) return false;
        const text = `${examDisplayTitle(exam)} ${paperNames.get(exam.paperId) || ''} ${examCategoryLabel(examCategory(exam))}`.toLowerCase();
        return terms.every(term => text.includes(term));
    }), filters.sort);
}
